import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CheckCheck, ChevronUp, Lightbulb, ThumbsUp, Trash2 } from 'lucide-react'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import { Card, CardContent } from '@/ui/components/common/Card'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { useDeleteSuggestion, useGetSuggestions } from '@/application/services/suggestion.service'
import type { Suggestion } from '@/domains/types'
import { markdownToPlainExcerpt } from '@/ui/utils/markdown'
import { ApproveDialog } from './ApproveDialog'

const SUGGESTIONS_LIMIT = 20

// ──────────────────────────────────────────────
// Public section — viewer-submitted suggestions table
// ──────────────────────────────────────────────

/**
 * Lists viewer-submitted investigation suggestions with sort (votes/date)
 * and pagination. Each row can be approved (turning it into an actual
 * investigation via the ApproveDialog) or deleted. Privileged-only — the
 * parent page is responsible for not rendering this otherwise.
 */
export function SuggestionsSection() {
  const { t } = useTranslation()

  const [suggestionSortBy, setSuggestionSortBy] = useState<'votes' | 'date'>('votes')
  const [suggestionPage, setSuggestionPage] = useState(1)
  const { data: suggestionsData, isLoading: suggestionsLoading } = useGetSuggestions(
    suggestionSortBy,
    suggestionPage,
    SUGGESTIONS_LIMIT,
  )
  const { mutateAsync: deleteSuggestion, isPending: isDeleting } = useDeleteSuggestion()
  const [approvingSuggestion, setApprovingSuggestion] = useState<Suggestion | null>(null)

  const suggestionTotalPages = Math.max(
    1,
    Math.ceil((suggestionsData?.total ?? 0) / SUGGESTIONS_LIMIT),
  )

  const handleDeleteSuggestion = async (suggestion: Suggestion) => {
    if (!window.confirm(t('admin.suggestions_delete_confirm'))) return
    try {
      await deleteSuggestion(suggestion.suggestionId)
      toast.success(t('admin.suggestions_deleted'))
      if (suggestionPage > 1 && (suggestionsData?.suggestions.length ?? 0) === 1) {
        setSuggestionPage((page) => page - 1)
      }
    } catch {
      toast.error(t('admin.suggestions_delete_failed'))
    }
  }

  return (
    <>
      <div>
        {/* Header — title + sort controls */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <div>
              <h2 className="text-xl font-bold">{t('admin.suggestions_page_title')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('admin.suggestions_page_subtitle')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={suggestionSortBy === 'votes' ? 'primary' : 'outline'}
              onClick={() => {
                setSuggestionSortBy('votes')
                setSuggestionPage(1)
              }}
              leftIcon={<ThumbsUp className="w-3.5 h-3.5" />}
            >
              {t('investigations.sort_most_voted')}
            </Button>
            <Button
              size="sm"
              variant={suggestionSortBy === 'date' ? 'primary' : 'outline'}
              onClick={() => {
                setSuggestionSortBy('date')
                setSuggestionPage(1)
              }}
            >
              {t('investigations.sort_newest')}
            </Button>
          </div>
        </div>

        {/* Body */}
        {suggestionsLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_item, index) => (
              <Skeleton key={index} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        ) : !suggestionsData?.suggestions.length ? (
          <Card>
            <CardContent className="text-center py-10">
              <Lightbulb className="w-10 h-10 mx-auto text-muted-foreground mb-3 opacity-40" />
              <p className="text-sm text-muted-foreground">{t('admin.suggestions_empty_title')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground w-1/4">
                    {t('admin.suggestions_col_title')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">
                    {t('admin.suggestions_col_description')}
                  </th>
                  <th className="px-4 py-3 text-center font-medium text-muted-foreground w-20">
                    <span className="flex items-center justify-center gap-1">
                      <ChevronUp className="w-3.5 h-3.5" />
                      {t('admin.suggestions_col_votes')}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden lg:table-cell w-36">
                    {t('admin.suggestions_col_by')}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden lg:table-cell w-28">
                    {t('admin.suggestions_col_date')}
                  </th>
                  <th className="px-4 py-3 w-36" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {suggestionsData.suggestions.map((suggestion) => (
                  <tr key={suggestion.suggestionId} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-medium align-top">
                      <span className="line-clamp-2">{suggestion.title}</span>
                      <div className="flex items-center gap-2 mt-1 lg:hidden">
                        <span className="text-xs text-muted-foreground">
                          {suggestion.suggestedBy}
                        </span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(suggestion.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground align-top hidden md:table-cell">
                      {suggestion.description ? (
                        <span className="line-clamp-2">
                          {markdownToPlainExcerpt(suggestion.description)}
                        </span>
                      ) : (
                        <span className="italic text-muted-foreground/60">
                          {t('admin.suggestions_no_description')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center align-top">
                      <Badge variant="secondary" className="tabular-nums">
                        {suggestion.voteCount}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground align-top hidden lg:table-cell">
                      <span className="truncate max-w-[8rem] block">{suggestion.suggestedBy}</span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground align-top hidden lg:table-cell whitespace-nowrap">
                      {new Date(suggestion.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => setApprovingSuggestion(suggestion)}
                          leftIcon={<CheckCheck className="w-3.5 h-3.5" />}
                        >
                          {t('admin.suggestions_approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteSuggestion(suggestion)}
                          isLoading={isDeleting}
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {suggestionTotalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSuggestionPage((page) => Math.max(1, page - 1))}
              disabled={suggestionPage === 1}
            >
              ←
            </Button>
            <span className="text-sm text-muted-foreground">
              {t('admin.pagination_info', {
                page: suggestionPage,
                total: suggestionTotalPages,
              })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSuggestionPage((page) => Math.min(suggestionTotalPages, page + 1))}
              disabled={suggestionPage === suggestionTotalPages}
            >
              →
            </Button>
          </div>
        )}
      </div>

      {approvingSuggestion && (
        <ApproveDialog
          suggestion={approvingSuggestion}
          open={!!approvingSuggestion}
          onClose={() => setApprovingSuggestion(null)}
        />
      )}
    </>
  )
}
